import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FlaskConical } from 'lucide-react';
import { toast } from 'react-toastify';
import SplashScreen from '@/components/common/SplashScreen';
import QuitCard from '@/components/common/asset/QuitCard';
import SurveyCard from '@/components/common/asset/SurveyCard';
import QuizCard from '@/components/common/asset/QuizCard';
import OfferCard from '@/components/common/asset/OfferCard';
import SharedResourceViewer from '@/components/pages/live/attendee/SharedResourceViewer';
import FooterBar from '@/components/pages/live/FootBar';
import EvergreenTestPlayer from '@/components/pages/live/evergreen-test/EvergreenTestPlayer';
import EvergreenTestChat, {
  type EvergreenTestPurchaseNotification,
} from '@/components/pages/live/evergreen-test/EvergreenTestChat';
import EvergreenTestEndCard from '@/components/pages/live/evergreen-test/EvergreenTestEndCard';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { WebinarLanguageProvider, useWebinarLng } from '@/contexts/WebinarLanguageContext';
import { formatPlannedLiveDate } from '@/lib/formatPlannedDate';
import type {
  EvergreenTestRuntime,
  ParticipationStatusDto,
  SharedResourcePublic,
  WebinarLanguage,
} from '@/features/webinar/types';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import { useExitIntent } from '@/hooks/useExitIntent';
import { useWebinarT } from '@/hooks/useWebinarT';
import {
  buildEvergreenTestTimeline,
  crossedEvergreenTestMoments,
  evergreenTestActionCountAt,
  evergreenTestStateAfter,
  isEvergreenTestFinishedAt,
} from '@/lib/evergreenTestTimeline';
import {
  evergreenTestClockSeconds,
  readEvergreenTestAnchor,
  resolveEvergreenTestError,
  resolveEvergreenTestStartMs,
  saveEvergreenTestAnchor,
  type EvergreenTestErrorState,
} from '@/lib/evergreenTestRun';
import { toEvergreenTestOffer } from '@/lib/evergreenTestOffer';
import {
  extractPurchaseNotificationOfferColors,
  resolvePurchaseNotificationDisplay,
} from '@/lib/purchaseNotificationDisplay';
import { cn } from '@/lib/utils';
import { webinarService } from '@/services/webinar.service';
import { workspaceService } from '@/services/workspace.service';

type FloatingReaction = {
  id: string;
  text: string;
  left: number;
  drift: number;
  rotateStart: number;
  rotateEnd: number;
};

type RoomData = {
  runtime: EvergreenTestRuntime;
  statuses: ParticipationStatusDto[];
  language: WebinarLanguage | null;
  visualIdentity: WorkspaceVisualIdentity;
};

type RoomLoad =
  | { status: 'loading' }
  | { status: 'error'; error: EvergreenTestErrorState }
  | { status: 'ready'; data: RoomData };

// Identité visuelle introuvable : rendu par défaut plutôt qu'un écran bloqué.
function defaultVisualIdentity(workspaceId: number): WorkspaceVisualIdentity {
  return {
    workspaceId,
    modified: false,
    logoUrl: null,
    fontFamily: null,
    theme: 'LIGHT',
    accentColor: null,
    backgroundColor: null,
    secondaryBackgroundColor: null,
    contrastColor: null,
    gradient1Color1: null,
    gradient1Color2: null,
    gradient2Color1: null,
    gradient2Color2: null,
    defaultIdentity: true,
  };
}

function useIsMobileViewport(): boolean {
  const query = '(max-width: 767px)';
  const [isMobile, setIsMobile] = useState(
    () => typeof window.matchMedia === 'function' && window.matchMedia(query).matches,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mediaQuery = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent) => setIsMobile(event.matches);
    mediaQuery.addEventListener('change', onChange);
    return () => mediaQuery.removeEventListener('change', onChange);
  }, []);
  return isMobile;
}

function EvergreenTestMessage({
  message,
  actionLabel,
  onAction,
}: {
  message: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <div className="bg-gradient-page-auth h-svh w-full flex flex-col items-center justify-center overflow-hidden px-6">
      <div className="flex flex-col items-center gap-8 max-w-lg text-center">
        <img
          src="/assets/welya-splash.png"
          alt="Welya"
          className="w-32 h-32 object-contain opacity-90"
        />
        <p className="text-xl font-semibold text-white">{message}</p>
        {actionLabel && onAction && (
          <Button
            type="button"
            onClick={onAction}
            className="bg-button-primary hover:bg-button-primary/90 text-dark font-semibold px-8 py-6 rounded-full shadow-lg"
          >
            {actionLabel}
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Salle de test d'un Evergreen publié, réservée aux administrateurs : rejoue la configuration
 * courante comme la verrait un spectateur, sans rien enregistrer ni appeler les routes spectateur.
 */
export default function EvergreenTestPage() {
  const { t } = useWebinarT('webinars');
  const params = useParams<{ workspaceId: string; webinarId: string; testRunId?: string }>();
  const workspaceId = Number(params.workspaceId);
  const webinarId = Number(params.webinarId);
  const testRunId = params.testRunId;
  const validIds = Number.isInteger(workspaceId) && Number.isInteger(webinarId);
  const navigate = useNavigate();
  const location = useLocation();
  const [startError, setStartError] = useState<EvergreenTestErrorState | null>(null);
  // Une création par navigation : le double effet de StrictMode ne doit pas lancer deux runs.
  const startedForLocationRef = useRef<string | null>(null);

  useEffect(() => {
    if (testRunId || !validIds) return;
    if (startedForLocationRef.current === location.key) return;
    startedForLocationRef.current = location.key;
    webinarService
      .startEvergreenTestRun(workspaceId, webinarId)
      .then((run) => {
        saveEvergreenTestAnchor(run.id, Date.now());
        navigate(`/evergreen-test/${workspaceId}/${webinarId}/${run.id}`, { replace: true });
      })
      .catch((error: unknown) => setStartError(resolveEvergreenTestError(error)));
  }, [testRunId, validIds, location.key, workspaceId, webinarId, navigate]);

  const startNewTest = useCallback(() => {
    setStartError(null);
    navigate(`/evergreen-test/${workspaceId}/${webinarId}`);
  }, [navigate, workspaceId, webinarId]);

  if (!validIds) return <EvergreenTestMessage message={t('evergreenTest.notPublished')} />;
  if (!testRunId) {
    if (startError) return <EvergreenTestError error={startError} onNewTest={startNewTest} />;
    return <SplashScreen label={t('evergreenTest.loading')} />;
  }
  return (
    <EvergreenTestRoomLoader
      key={testRunId}
      workspaceId={workspaceId}
      webinarId={webinarId}
      testRunId={testRunId}
      onNewTest={startNewTest}
    />
  );
}

function EvergreenTestError({
  error,
  onNewTest,
  onRetry,
}: {
  error: EvergreenTestErrorState;
  onNewTest: () => void;
  onRetry?: () => void;
}) {
  const { t } = useWebinarT('webinars');
  switch (error) {
    case 'FORBIDDEN':
      return <EvergreenTestMessage message={t('evergreenTest.forbidden')} />;
    case 'NOT_PUBLISHED':
      return <EvergreenTestMessage message={t('evergreenTest.notPublished')} />;
    case 'NOT_READY':
      return <EvergreenTestMessage message={t('evergreenTest.notReady')} />;
    case 'EXPIRED':
      return (
        <EvergreenTestMessage
          message={t('evergreenTest.expired')}
          actionLabel={t('evergreenTest.newTest')}
          onAction={onNewTest}
        />
      );
    case 'RETRY':
      return (
        <EvergreenTestMessage
          message={t('evergreenTest.loadError')}
          actionLabel={t('evergreenTest.retry')}
          onAction={onRetry ?? onNewTest}
        />
      );
  }
}

function EvergreenTestRoomLoader({
  workspaceId,
  webinarId,
  testRunId,
  onNewTest,
}: {
  workspaceId: number;
  webinarId: number;
  testRunId: string;
  onNewTest: () => void;
}) {
  const { t } = useWebinarT('webinars');
  const [attempt, setAttempt] = useState(0);
  const [load, setLoad] = useState<RoomLoad>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      webinarService.getEvergreenTestRuntime(workspaceId, webinarId, testRunId),
      // Libellé, couleur et icône des rôles, que le runtime ne porte pas : facultatif.
      webinarService
        .getParticipationStatuses(workspaceId, webinarId)
        .then((config) => config.statuses)
        .catch(() => []),
      webinarService
        .getById(workspaceId, webinarId)
        .then((webinar) => webinar.language)
        .catch(() => null),
      // Purement cosmétique : une erreur ne doit pas bloquer la salle.
      workspaceService.getVisualIdentity(workspaceId).catch(() => null),
    ])
      .then(([runtime, statuses, language, visualIdentity]) => {
        if (cancelled) return;
        setLoad({
          status: 'ready',
          data: {
            runtime,
            statuses,
            language,
            visualIdentity: visualIdentity ?? defaultVisualIdentity(workspaceId),
          },
        });
      })
      .catch((error: unknown) => {
        if (!cancelled) setLoad({ status: 'error', error: resolveEvergreenTestError(error) });
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId, webinarId, testRunId, attempt]);

  const retry = () => {
    setLoad({ status: 'loading' });
    setAttempt((value) => value + 1);
  };
  const markExpired = useCallback(() => setLoad({ status: 'error', error: 'EXPIRED' }), []);

  if (load.status === 'loading') return <SplashScreen label={t('evergreenTest.loading')} />;
  if (load.status === 'error') {
    return <EvergreenTestError error={load.error} onNewTest={onNewTest} onRetry={retry} />;
  }
  return (
    <WebinarLanguageProvider language={load.data.language}>
      <EvergreenTestRoom
        data={load.data}
        workspaceId={workspaceId}
        webinarId={webinarId}
        onRestart={onNewTest}
        onExpired={markExpired}
      />
    </WebinarLanguageProvider>
  );
}

function EvergreenTestRoom({
  data,
  workspaceId,
  webinarId,
  onRestart,
  onExpired,
}: {
  data: RoomData;
  workspaceId: number;
  webinarId: number;
  onRestart: () => void;
  onExpired: () => void;
}) {
  const { t } = useWebinarT('webinars');
  const { runtime, statuses, visualIdentity } = data;
  const { snapshot } = runtime;
  const webinarLng = useWebinarLng();
  const formattedStartDate = useMemo(
    () => formatPlannedLiveDate(runtime.startedAt, webinarLng),
    [runtime.startedAt, webinarLng],
  );
  const isMobile = useIsMobileViewport();

  const timeline = useMemo(
    () => buildEvergreenTestTimeline(snapshot, statuses),
    [snapshot, statuses],
  );
  const [startMs] = useState(
    () =>
      resolveEvergreenTestStartMs({
        anchorMs: readEvergreenTestAnchor(runtime.testRunId),
        startedAt: runtime.startedAt,
      }) ?? Date.now(),
  );
  // Rechargement après la fin : la carte de fin s'affiche directement.
  const [finished, setFinished] = useState(() =>
    isEvergreenTestFinishedAt(
      timeline,
      evergreenTestClockSeconds(startMs, Date.now(), snapshot.durationSeconds) * 1000,
    ),
  );
  const [actionCount, setActionCount] = useState(0);
  const [footerSeconds, setFooterSeconds] = useState(0);
  const lastPositionRef = useRef<number | null>(null);
  const notificationIdRef = useRef(0);
  const [notifications, setNotifications] = useState<EvergreenTestPurchaseNotification[]>([]);
  const [reactions, setReactions] = useState<FloatingReaction[]>([]);
  const [submittedSurveyIds, setSubmittedSurveyIds] = useState<ReadonlySet<number>>(new Set());
  const [submittedQuizIds, setSubmittedQuizIds] = useState<ReadonlySet<number>>(new Set());
  const [hasReached2Min, setHasReached2Min] = useState(false);

  const state = useMemo(() => evergreenTestStateAfter(timeline, actionCount), [timeline, actionCount]);

  const offer = useMemo(() => {
    const template = snapshot.offers.find((candidate) => candidate.id === state.offerId);
    return template ? toEvergreenTestOffer(template) : null;
  }, [snapshot.offers, state.offerId]);
  // Lien et embed réels : un rendez-vous ou un paiement y serait vraiment enregistré.
  const handleOfferClick = useCallback(() => toast.info(t('evergreenTest.offerDisabled')), [t]);
  const survey =
    state.surveyId != null && !submittedSurveyIds.has(state.surveyId)
      ? (snapshot.surveys.find((candidate) => candidate.id === state.surveyId) ?? null)
      : null;
  const quiz =
    state.quizId != null && !submittedQuizIds.has(state.quizId)
      ? (snapshot.quizzes.find((candidate) => candidate.id === state.quizId) ?? null)
      : null;
  const sharedResources = useMemo<SharedResourcePublic[]>(
    () =>
      state.sharedResourceIds.flatMap((id) => {
        const resource = snapshot.sharedResources.find((candidate) => candidate.id === id);
        return resource
          ? [
              {
                id: resource.id,
                type: resource.type,
                title: resource.title,
                description: resource.description,
                originalFileName: resource.originalFileName,
                mimeType: resource.mimeType,
                sizeBytes: resource.sizeBytes,
                externalUrl: resource.externalUrl,
                live: true,
              },
            ]
          : [];
      }),
    [snapshot.sharedResources, state.sharedResourceIds],
  );
  const hasVisibleInteraction = !!(offer || survey || quiz);

  const evergreenMessage = t('live.chat.purchaseNotificationEvergreen');
  const handlePositionChange = useCallback(
    (positionMs: number) => {
      const previousMs = lastPositionRef.current;
      lastPositionRef.current = positionMs;
      setActionCount(evergreenTestActionCountAt(timeline, positionMs));
      setFooterSeconds(Math.max(0, Math.round(positionMs / 1000)));
      if (isEvergreenTestFinishedAt(timeline, positionMs)) setFinished(true);
      if (previousMs == null) return;

      const crossed = crossedEvergreenTestMoments(timeline, previousMs, positionMs);
      const queued = crossed.purchaseNotifications.flatMap((payload) => {
        const display = resolvePurchaseNotificationDisplay(payload, {
          viewer: 'ATTENDEE',
          attendeeEmail: snapshot.viewer.email ?? '',
          evergreenMessage,
        });
        if (!display) return [];
        notificationIdRef.current += 1;
        return [
          {
            id: notificationIdRef.current,
            message: display.message,
            icon: display.icon,
            showIcon: display.showIcon,
            offerColors: extractPurchaseNotificationOfferColors(display.colorSource),
          },
        ];
      });
      if (queued.length > 0) setNotifications((queue) => [...queue, ...queued]);

      for (const text of crossed.liveReactions) {
        const reaction: FloatingReaction = {
          id: crypto.randomUUID(),
          text,
          left: Math.random() * 20,
          drift: (Math.random() - 0.5) * 140,
          rotateStart: (Math.random() - 0.5) * 24,
          rotateEnd: (Math.random() - 0.5) * 24,
        };
        setReactions((current) => [...current, reaction]);
        window.setTimeout(() => {
          setReactions((current) => current.filter((item) => item.id !== reaction.id));
        }, 3000);
      }
    },
    [timeline, snapshot.viewer.email, evergreenMessage],
  );
  const handleEnded = useCallback(() => setFinished(true), []);
  const handleNotificationDone = useCallback(
    () => setNotifications((queue) => queue.slice(1)),
    [],
  );

  const requestDownloadUrl = useCallback(
    (sharedResourceId: number) =>
      webinarService
        .getEvergreenTestSharedResourceDownloadUrl(
          workspaceId,
          webinarId,
          runtime.testRunId,
          sharedResourceId,
        )
        .catch((error: unknown) => {
          if (resolveEvergreenTestError(error) === 'EXPIRED') onExpired();
          throw error;
        }),
    [workspaceId, webinarId, runtime.testRunId, onExpired],
  );

  const quitPage = snapshot.quitPage;
  const canShowExitIntentPopup = !!quitPage?.showPopup && hasReached2Min;
  const { showExitIntent, reset } = useExitIntent({
    enabled: canShowExitIntentPopup && !isMobile && !finished,
  });

  const footer = (
    <FooterBar
      // Seul spectateur de la salle de test : l'administrateur.
      liveParticipantCount={1}
      // Test terminé : le bandeau passe hors ligne au lieu d'un direct figé.
      liveStartAt={finished ? undefined : runtime.startedAt}
      isAttendee
      isEvergreen
      evergreenElapsedDisplaySeconds={footerSeconds}
      visualIdentity={visualIdentity}
      displayParticipantsMinThreshold={snapshot.displayParticipantsMinThreshold ?? 0}
      displayParticipantsCountEnabled={snapshot.displayParticipantsCountEnabled}
      onLiveDurationReached2Min={setHasReached2Min}
      formattedPlannedDate={formattedStartDate}
    />
  );

  const markSurveySubmitted = (surveyId: number) =>
    setSubmittedSurveyIds((ids) => new Set(ids).add(surveyId));
  const markQuizSubmitted = (quizId: number) =>
    setSubmittedQuizIds((ids) => new Set(ids).add(quizId));
  const isQuizInline = quiz?.enabledDuringLive === false || snapshot.chatMode === 'DISABLED';

  return (
    <div
      className="h-svh max-w-[2400px] mx-auto w-full flex flex-col lg:flex-row lg:justify-center lg:gap-6 overflow-hidden min-h-0 compact-landscape:flex-row compact-landscape:justify-center compact-landscape:gap-4 compact-landscape:pl-[env(safe-area-inset-left)] compact-landscape:pr-[env(safe-area-inset-right)]"
      style={{ backgroundColor: visualIdentity.backgroundColor ?? '#FFFFFF' }}
    >
      {!isMobile && hasReached2Min && quitPage?.showPopup && (
        <Dialog open={showExitIntent} onOpenChange={(open) => !open && reset()}>
          <DialogHeader className="hidden">
            <DialogTitle>{t('live.exitDialogTitle')}</DialogTitle>
          </DialogHeader>
          <DialogContent
            className="p-0 max-w-2xl w-fit max-h-[90vh] overflow-y-auto border-none shadow-none bg-transparent"
            hideCloseButton={false}
          >
            {/* Sans session ni jeton, la carte n'envoie rien : la réponse ferme seulement la popup. */}
            <QuitCard
              quitPage={quitPage}
              onSubmit={reset}
              onCancel={reset}
              visualIdentity={visualIdentity}
            />
          </DialogContent>
        </Dialog>
      )}
      <div className="flex shrink-0 flex-col gap-4 lg:m-0 lg:min-w-[300px] lg:flex-2 lg:justify-between lg:pl-6 lg:py-6 lg:h-full lg:overflow-y-hidden compact-landscape:min-w-0 compact-landscape:flex-2 compact-landscape:justify-between compact-landscape:h-full compact-landscape:overflow-y-hidden">
        <div className="flex flex-col items-center gap-4 lg:flex-1 lg:min-h-0 lg:h-full compact-landscape:flex-1 compact-landscape:min-h-0 compact-landscape:h-full">
          <div className="w-full relative lg:flex-1 lg:min-h-0 lg:h-full lg:flex lg:items-stretch lg:justify-stretch compact-landscape:flex-1 compact-landscape:min-h-0 compact-landscape:h-full compact-landscape:flex compact-landscape:items-stretch compact-landscape:justify-stretch">
            <div className="absolute top-3 left-3 z-40 pointer-events-none flex items-center gap-1.5 rounded-full bg-black/70 px-3 py-1 text-xs font-semibold text-white">
              <FlaskConical className="size-3.5" aria-hidden />
              {t('evergreenTest.badge')}
            </div>
            {finished ? (
              <EvergreenTestEndCard visualIdentity={visualIdentity} onRestart={onRestart} />
            ) : (
              <EvergreenTestPlayer
                videoUrl={snapshot.videoUrl}
                fallbackVideoUrl={snapshot.fallbackVideoUrl}
                startMs={startMs}
                durationSeconds={snapshot.durationSeconds}
                visualIdentity={visualIdentity}
                hasVisibleInteraction={hasVisibleInteraction}
                onPositionChange={handlePositionChange}
                onEnded={handleEnded}
              />
            )}
            {reactions.map((reaction) => (
              <div
                key={reaction.id}
                className="absolute hidden md:block bottom-0 translate-y-[-50%] pointer-events-none z-50 animate-rise-up-tiktok"
                style={{
                  left: `${reaction.left}%`,
                  ['--drift' as string]: `${reaction.drift}px`,
                  ['--rotate-start' as string]: `${reaction.rotateStart}deg`,
                  ['--rotate-end' as string]: `${reaction.rotateEnd}deg`,
                }}
              >
                <div className="text-2xl">{reaction.text}</div>
              </div>
            ))}
          </div>
          <div className="w-full lg:flex shrink-0 hidden justify-center md:justify-between lg:justify-start gap-4">
            <div className="items-center justify-between hidden md:block lg:hidden">{footer}</div>
          </div>
        </div>
        <div className="hidden lg:block lg:items-center lg:shrink-0 w-full">{footer}</div>
      </div>
      <div
        className={cn(
          'block flex-1 min-h-0',
          'lg:min-w-[280px] lg:max-w-[456px] lg:flex-1 lg:pr-6 lg:h-svh',
          'compact-landscape:min-w-[280px] compact-landscape:max-w-[456px] compact-landscape:flex-1 compact-landscape:h-full',
        )}
      >
        <div className="h-full flex flex-col min-h-0 relative">
          <EvergreenTestChat
            visualIdentity={visualIdentity}
            state={state}
            evergreenChatMode={snapshot.chatMode}
            offer={offer}
            survey={survey}
            quiz={quiz}
            sharedResources={sharedResources}
            onOfferClick={handleOfferClick}
            onSurveySubmit={markSurveySubmitted}
            onQuizSubmit={markQuizSubmitted}
            requestDownloadUrl={requestDownloadUrl}
            notification={notifications[0] ?? null}
            hasMoreNotifications={notifications.length > 1}
            onNotificationDone={handleNotificationDone}
          />
        </div>
      </div>
      {/* Interactions mobiles et tablette, entre le chat et le pied de page. */}
      {survey && (
        <div className="lg:hidden compact-landscape:hidden block shrink-0 overflow-y-auto max-h-[50dvh]">
          <SurveyCard
            survey={survey}
            visualIdentity={visualIdentity}
            isAttendee
            onLocalSubmit={() => markSurveySubmitted(survey.id)}
          />
        </div>
      )}
      {quiz && (
        <div className="lg:hidden compact-landscape:hidden block shrink-0 overflow-y-auto max-h-[50dvh]">
          {isQuizInline ? (
            <QuizCard
              quiz={quiz}
              visualIdentity={visualIdentity}
              isAttendee
              onLocalSubmit={() => markQuizSubmitted(quiz.id)}
            />
          ) : (
            <div className="flex flex-col justify-between">
              <div className="flex-1 flex items-center justify-center px-0 md:px-4">
                <QuizCard
                  quiz={quiz}
                  visualIdentity={visualIdentity}
                  isAttendee
                  onLocalSubmit={() => markQuizSubmitted(quiz.id)}
                />
              </div>
            </div>
          )}
        </div>
      )}
      {offer && (
        <div className="lg:hidden compact-landscape:hidden block shrink-0 overflow-y-auto max-h-[50dvh]">
          <OfferCard
            offer={offer}
            visualIdentity={visualIdentity}
            attendeeView
            onLocalClick={handleOfferClick}
          />
        </div>
      )}
      {sharedResources.length > 0 && (
        <div className="lg:hidden compact-landscape:hidden block shrink-0 overflow-y-auto max-h-[50dvh]">
          <SharedResourceViewer
            webinarId={0}
            sessionIdentifier=""
            authorization=""
            visualIdentity={visualIdentity}
            resources={sharedResources}
            requestDownloadUrl={requestDownloadUrl}
          />
        </div>
      )}
      <div className="bottom-0 mx-0 my-0 lg:hidden compact-landscape:hidden flex justify-center lg:shrink-0 w-full pb-[max(0px,env(safe-area-inset-bottom))]">
        {footer}
      </div>
    </div>
  );
}
